import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import NotFound from '../../pages/system/NotFound';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { fetchDMAutomation } from '../../redux-stores/slices/dmAutomationSlice';
import withModal from '../../utils/hoc/withModal';
import PostSkeleton from '../../skeletons/PostSkeleton';
import { POSTAPIError } from '../../types';
import LinkPostFields from './LinkPostFields';
import { LinkDataType } from './AddLink';
import { ERROR } from '../../constants/errors';
import { deleteDMAutomation, updateDMAutomation } from '../../services/api/dmAutomations';
import { toast } from 'react-toastify';
import { fetchDMAutomations } from '../../redux-stores/slices/dmAutomationsListSlice';
import { copyToClipboard } from '../../utils/helpers';
import withSlideUpModal from '../../utils/hoc/withSlideUpModalComponent';

interface AutomationInsightsProps {
    postId: string | null,
    onClose: () => void,
    onError: (error: POSTAPIError) => void
    onEdit: () => void
}

const AutomationInsights: React.FC<AutomationInsightsProps> = ({ postId, onClose, onError, onEdit }) => {

    const dispatch: AppDispatch = useDispatch();

    const dmAutomation = useSelector((state: RootState) => state.dmAutomations.items[postId!]?.dmAutomation ?? null);

    const loading = useSelector((state: RootState) => state.dmAutomations.loading);

    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [postError, setPOSTError] = React.useState<POSTAPIError | null>(null);

    const [isProcessing, setProcessing] = React.useState(false);

    const [deleteToast, setDeleteToast] = useState<string | number | null>(null);

    const selectedSocialAccount = useSelector((state: RootState) => state.dmAutomationsList.selectedSocialAccount);

    const [link, setLink] = useState<LinkDataType>({
        post: dmAutomation?.automation?.social_post.uuid ?? null,
        triggers: dmAutomation?.automation?.triggers ?? [],
        url: dmAutomation?.automation?.url ?? '',
        button_text: dmAutomation?.automation?.button_text ?? '',
        historical: dmAutomation?.automation?.historical ?? false,
        active: dmAutomation?.automation?.active ?? true
    });


    useEffect(() => {
        const setDefaults = () => {
            setLink({
                post: dmAutomation?.automation?.social_post.uuid ?? null,
                triggers: dmAutomation?.automation?.triggers ?? [],
                url: dmAutomation?.automation?.url ?? '',
                button_text: dmAutomation?.automation?.button_text ?? '',
                historical: dmAutomation?.automation?.historical ?? false,
                active: dmAutomation?.automation?.active ?? true
            })
            setPOSTError(null);
            setProcessing(false);
        }
        setDefaults();
    }, [dmAutomation, editMode])

    const fetchDMAutomationData = useCallback(async () => {
        if (!postId) return;
        dispatch(fetchDMAutomation({ postId }));
        setEditMode(false);
    }, [postId, dispatch])

    useEffect(() => {
        fetchDMAutomationData()
    }, [postId, fetchDMAutomationData])

    const updateLink = async () => {
        try {
            setProcessing(true);
            await updateDMAutomation(postId!, { button_text: link.button_text, triggers: link.triggers });
            toast.success("Smartlink updated successfully!");
            dispatch(fetchDMAutomation({ postId: postId!, force: true }));
            if (selectedSocialAccount) {
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
            }
        } catch (error: any) {
            if (error.response.data) {
                setPOSTError(error.response.data);
            }
        } finally {
            setEditMode(false)
        }
    }

    const removeLink = async (toastId: string | number) => {
        try {
            if (dmAutomation.automation.status === 'inactive') return;
            setProcessing(true);
            await deleteDMAutomation(postId!);
            toast.success("Smartlink removed successfully!");
            dispatch(fetchDMAutomation({ postId: postId!, force: true }));
            if (selectedSocialAccount) {
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
            }
            onClose();
        } catch (error: any) {
            if (error.response.data) {
                setPOSTError(error.response.data);
            }
        } finally {
            dismissDeletionToast(toastId);
        }
    }

    const dismissDeletionToast = (toastId: string | number) => {
        toast.dismiss(toastId);
        setDeleteToast(null);
    }

    const handleDelete = () => {
        if (deleteToast || dmAutomation.automation.status === 'inactive') return;

        const toastId = toast.warning(
            <div className='d-flex justify-content-between align-items-center w-100'>
                <span className='font-12'>Are you sure you want to remove this link?</span>
                <div className='d-flex gap-2'>
                    <button className='toaster-confirm-btn' onClick={() => removeLink(toastId)}>Yes</button>
                    <button className='toaster-decline-btn' onClick={() => dismissDeletionToast(toastId)}>No</button>
                </div>
            </div>,
            { autoClose: false, closeOnClick: false, position: "bottom-center", closeButton: false }
        );

        setDeleteToast(toastId);
    };

    const onPostError = useCallback(async () => {
        if (postError && postError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) {
            onError(postError);
        }
    }, [postError, onError])

    useEffect(() => {
        onPostError();
    }, [postError, onPostError])


    if (!postId) return <NotFound />

    if (loading) return <PostSkeleton />

    return (
        <div>
            {
                dmAutomation &&
                <React.Fragment>
                    {
                        dmAutomation.automation.status === 'active' &&
                        <div className="d-flex align-items-center justify-content-end w-100">
                            <button className='btn btn-link' onClick={() => onEdit()}>
                                <span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.91052 1.76283e-07L10.2083 3.74965e-07C10.5535 3.74965e-07 10.8333 0.279822 10.8333 0.625C10.8333 0.970178 10.5535 1.25 10.2083 1.25H8.95833C6.97648 1.25 5.55296 1.25133 4.46965 1.39698C3.40431 1.54021 2.76204 1.81262 2.28733 2.28733C1.81262 2.76204 1.54021 3.40431 1.39698 4.46965C1.25133 5.55296 1.25 6.97648 1.25 8.95833C1.25 10.9402 1.25133 12.3637 1.39698 13.447C1.54021 14.5124 1.81262 15.1546 2.28733 15.6293C2.76204 16.104 3.40431 16.3765 4.46965 16.5197C5.55296 16.6653 6.97648 16.6667 8.95833 16.6667C10.9402 16.6667 12.3637 16.6653 13.447 16.5197C14.5124 16.3765 15.1546 16.104 15.6293 15.6293C16.104 15.1546 16.3765 14.5124 16.5197 13.447C16.6653 12.3637 16.6667 10.9402 16.6667 8.95833V7.70833C16.6667 7.36316 16.9465 7.08333 17.2917 7.08333C17.6368 7.08333 17.9167 7.36316 17.9167 7.70833V9.00614C17.9167 10.9298 17.9167 12.4373 17.7585 13.6136C17.5967 14.8175 17.2589 15.7675 16.5132 16.5132C15.7675 17.2589 14.8175 17.5967 13.6136 17.7585C12.4373 17.9167 10.9298 17.9167 9.00614 17.9167H8.91052C6.98686 17.9167 5.47933 17.9167 4.30309 17.7585C3.09914 17.5967 2.14912 17.2589 1.40345 16.5132C0.657772 15.7675 0.319989 14.8175 0.158122 13.6136C-1.90486e-05 12.4373 -1.05526e-05 10.9298 1.76283e-07 9.00615V8.91052C-1.05526e-05 6.98686 -1.90486e-05 5.47933 0.158122 4.30309C0.319989 3.09914 0.657772 2.14912 1.40345 1.40345C2.14912 0.657772 3.09914 0.319989 4.30309 0.158122C5.47933 -1.90486e-05 6.98686 -1.05526e-05 8.91052 1.76283e-07ZM12.9338 0.854929C14.0737 -0.284976 15.9218 -0.284976 17.0617 0.854929C18.2016 1.99483 18.2016 3.84299 17.0617 4.98289L11.5217 10.523C11.2123 10.8324 11.0184 11.0263 10.8022 11.195C10.5474 11.3937 10.2718 11.564 9.98012 11.703C9.73251 11.821 9.47246 11.9077 9.05732 12.046L6.63688 12.8528C6.19001 13.0018 5.69734 12.8855 5.36426 12.5524C5.03118 12.2193 4.91488 11.7267 5.06383 11.2798L5.87064 8.85936C6.00899 8.44421 6.09565 8.18416 6.21366 7.93654C6.35266 7.6449 6.52301 7.36926 6.7217 7.11452C6.89041 6.89822 7.08425 6.70441 7.3937 6.395L12.9338 0.854929ZM16.1779 1.73881C15.5261 1.08706 14.4694 1.08706 13.8177 1.73881L13.5038 2.05266C13.5227 2.13255 13.5492 2.22773 13.586 2.33389C13.7054 2.6781 13.9314 3.13143 14.3583 3.55833C14.7852 3.98523 15.2386 4.21122 15.5828 4.33064C15.6889 4.36747 15.7841 4.39394 15.864 4.41286L16.1779 4.09901C16.8296 3.44726 16.8296 2.39056 16.1779 1.73881ZM14.8793 5.39757C14.4493 5.21266 13.9485 4.91622 13.4745 4.44221C13.0005 3.96821 12.704 3.46735 12.5191 3.03738L8.30626 7.25022C7.95916 7.59731 7.82304 7.73496 7.70734 7.88329C7.56448 8.06645 7.442 8.26463 7.34207 8.47432C7.26114 8.64413 7.1989 8.82744 7.04367 9.29312L6.68376 10.3729L7.54381 11.2329L8.62355 10.873C9.08922 10.7178 9.27254 10.6555 9.44235 10.5746C9.65204 10.4747 9.85022 10.3522 10.0334 10.2093C10.1817 10.0936 10.3194 9.95751 10.6665 9.61041L14.8793 5.39757Z" fill="var(--primary)" />
                                    </svg>
                                </span>

                                Edit Link
                            </button>
                        </div>
                    }
                    <div className="divider my-3"></div>
                    <div className='position-relative rounded-4 overflow-hidden'>
                        <img src={dmAutomation.automation.social_post.thumbnail_url} alt="post" className='post' />
                        {
                            dmAutomation.automation.status === 'active' &&
                            <div className='position-absolute linked-badge text-success'>
                                Linked
                            </div>
                        }
                        {
                            dmAutomation.automation.status === 'inactive' &&
                            <div className='position-absolute inactive-badge text-danger'>
                                Inactive
                            </div>
                        }
                        {
                            dmAutomation.automation.status === 'active' &&
                            <div className="post-action text-primary rounded-4">
                                <span className='cursor-pointer mb-2' onClick={handleDelete}><i className="fa-solid fa-trash me-2"></i>Remove Link</span>
                            </div>
                        }

                    </div>
                    <div className="m-0 pb-4">
                        <div className="card-body text-start">
                            <span className="d-block text-muted font-10">{dmAutomation.automation.social_post.posted_on}</span>
                            {dmAutomation.automation.social_post.ig?.caption && <p style={{ height: '50px' }}>{dmAutomation.automation.social_post.ig?.caption?.slice(0, 100)}{(dmAutomation.automation.social_post.ig?.caption?.length ?? 0) > 100 ? '...' : ''}</p>}
                        </div>
                        <div className="d-flex mt-2 gap-">
                            <button className="btn btn-outline-primary w-100" onClick={() => copyToClipboard(dmAutomation.automation.url, "Link copied to your clipboard!")}>
                                <i className="fa-solid fa-copy"></i> Copy Link
                            </button>
                        </div>
                        {
                            editMode ? (
                                <div className='mt-3 mb-1'>
                                    <LinkPostFields
                                        link={link}
                                        setLink={setLink}
                                        disableSubmission={isProcessing}
                                        onCancel={() => setEditMode(false)}
                                        onSubmit={updateLink}
                                        editMode={true}
                                        viewOnly={dmAutomation.automation.status === 'inactive'}
                                        post={dmAutomation.automation.social_post}
                                    />
                                </div>) : (
                                <div className="insights my-3">
                                    <h4 className="text-dark mb-2">Insights</h4>
                                    <div className="d-flex gap-2 mb-2">
                                        <div className="app-sub-card m-0 shadow-none p-3 w-100">
                                            <span className="fs-12 text-muted fw-400">Sent</span>
                                            <h1 className='fs-16 fw-600 text-dark'>{dmAutomation.stats.sent}</h1>
                                        </div>
                                        <div className="app-sub-card m-0 shadow-none p-3 w-100">
                                            <span className="fs-12 text-muted fw-400">Clicks</span>
                                            <h1 className='fs-16 fw-600 text-dark'>{dmAutomation.stats.clicks}</h1>
                                        </div>
                                    </div>
                                    <div className="app-sub-card m-0 shadow-none p-3 w-100">
                                        <span className="fs-12 text-muted fw-400">CTR</span>
                                        <h1 className='fs-16 fw-600 text-dark'>{dmAutomation.stats.ctr}%</h1>
                                    </div>
                                </div>)
                        }

                    </div>
                </React.Fragment>
            }
        </div>
    )
}

export default withSlideUpModal(AutomationInsights, "", false)